import { OPEN_COOKIE_SETTINGS_EVENT } from './CookieConsent';

const footerLinks = [
  { label: 'Work', href: '#portfolio' },
  { label: 'Services', href: '#services' },
  { label: 'Contact', href: 'mailto:info@consltr.com' },
];

const legalLinks = [
  { label: 'Privacy', href: '/privacy/' },
  { label: 'Terms', href: '/terms/' },
  { label: 'Cookies', href: '/cookies/' },
];

const socialLinks = [
  {
    label: 'LinkedIn',
    shortLabel: 'in',
    href: 'https://www.linkedin.com/company/consltr/',
  },
  {
    label: 'X',
    shortLabel: 'X',
    href: 'https://x.com/consltr',
  },
];

interface SiteFooterProps {
  homeHref?: string;
  sectionHrefPrefix?: string;
  currentPath?: string;
}

const SiteFooter = ({
  homeHref = '#top',
  sectionHrefPrefix = '',
  currentPath = '/',
}: SiteFooterProps) => {
  const openCookieSettings = () => {
    window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS_EVENT));
  };

  return (
    <footer className="border-t border-line/10 bg-canvas">
      <div className="mx-auto flex max-w-7xl flex-col gap-8 px-6 py-10 sm:flex-row sm:items-end sm:justify-between lg:px-8">
        <div>
          <a href={homeHref} className="serif text-2xl font-semibold text-ink">
            Consltr<span className="text-accent">.</span>
          </a>
          <p className="mt-3 max-w-sm text-sm leading-6 text-faint">
            Strategy, product, and technology consulting for ambitious ideas.
          </p>
        </div>
        <div className="flex flex-col gap-5 sm:items-end">
          <div className="flex flex-wrap items-center justify-between gap-5 sm:justify-end">
            <nav aria-label="Footer navigation" className="flex flex-wrap gap-x-6 gap-y-3">
              {footerLinks.map(link => (
                <a
                  key={link.label}
                  href={link.href.startsWith('#') ? `${sectionHrefPrefix}${link.href}` : link.href}
                  data-agent-id={`agent://consltr/footer/${link.label.toLowerCase()}`}
                  data-semantic-intent="navigate"
                  className="text-sm text-muted transition-colors hover:text-ink"
                >
                  {link.label}
                </a>
              ))}
            </nav>
            <nav aria-label="Social links" className="flex gap-2">
              {socialLinks.map(link => (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex h-9 min-w-9 items-center justify-center rounded-full border border-line/10 px-2 font-mono text-xs font-semibold text-faint transition-all duration-300 hover:-translate-y-1 hover:border-accent/60 hover:text-accent"
                  aria-label={`Consltr on ${link.label}`}
                  data-agent-id={`agent://consltr/footer/social/${link.label.toLowerCase()}`}
                  data-semantic-intent="open"
                >
                  {link.shortLabel}
                </a>
              ))}
            </nav>
          </div>
          <nav aria-label="Legal and privacy navigation" className="flex flex-wrap gap-x-5 gap-y-3">
            {legalLinks.map(link => (
              <a
                key={link.label}
                href={link.href}
                aria-current={currentPath.startsWith(link.href) ? 'page' : undefined}
                data-agent-id={`agent://consltr/footer/legal/${link.label.toLowerCase()}`}
                data-semantic-intent="navigate"
                className="text-xs text-faint underline decoration-line/30 underline-offset-4 transition-colors hover:text-ink"
              >
                {link.label}
              </a>
            ))}
            <button
              type="button"
              onClick={openCookieSettings}
              className="text-xs text-faint underline decoration-line/30 underline-offset-4 transition-colors hover:text-ink"
              data-agent-id="agent://consltr/footer/legal/cookie-settings"
              data-semantic-intent="open"
            >
              Cookie settings
            </button>
          </nav>
          <p className="text-xs text-faint">&copy; {new Date().getFullYear()} Consltr, LLC.</p>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
